import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { acceptInvite } from "@/lib/team.functions";
import { supabase } from "@/integrations/supabase/client";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo } from "@/lib/seo";
import { Check, Loader2, Users } from "lucide-react";

export const Route = createFileRoute("/join/$token")({
  head: () =>
    buildSeo({
      path: "/join",
      title: "Join your team — Reality Twin",
      description: "Accept your invitation and join your company's Reality Twin workspace.",
      noindex: true,
    }),
  component: JoinPage,
});

type State = "checking" | "signin" | "accepting" | "done" | "error";

function JoinPage() {
  const t = useHydratedTranslation();
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const accept = useServerFn(acceptInvite);
  const [state, setState] = useState<State>("checking");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!data.user) {
        setState("signin");
        return;
      }
      setState("accepting");
      try {
        const r = await accept({ data: { token } });
        if (cancelled) return;
        setMessage(r.companyName);
        setState("done");
        setTimeout(() => {
          if (!cancelled) navigate({ to: "/dashboard" });
        }, 1500);
      } catch (e: any) {
        if (cancelled) return;
        setMessage(e?.message ?? "Invitation could not be accepted.");
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, accept, navigate]);

  return (
    <Layout>
      <section className="mx-auto max-w-lg px-6 py-32 text-center">
        <div className="glass-strong rounded-2xl p-10">
          {state === "checking" || state === "accepting" ? (
            <>
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-[oklch(0.82_0.18_168)]" />
              <p className="mt-4 text-sm text-muted-foreground">
                {state === "checking"
                  ? t("routes.auth.joinChecking")
                  : t("routes.auth.joinJoining")}
              </p>
            </>
          ) : state === "signin" ? (
            <>
              <Users className="h-8 w-8 mx-auto text-[oklch(0.82_0.18_168)]" />
              <h1 className="mt-4 font-display text-2xl font-semibold">
                {t("routes.auth.joinInvited")}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("routes.auth.joinInvitedSub")}
              </p>
              <Link
                to="/auth"
                className="mt-6 inline-flex rounded-full brand-gradient text-black px-6 py-2.5 text-sm font-medium"
              >
                {t("routes.auth.joinSignIn")}
              </Link>
            </>
          ) : state === "done" ? (
            <>
              <div className="h-12 w-12 mx-auto rounded-full brand-gradient flex items-center justify-center text-black">
                <Check className="h-6 w-6" />
              </div>
              <h1 className="mt-4 font-display text-2xl font-semibold">
                {t("routes.auth.joinWelcome", { name: message })}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">{t("routes.auth.joinRedirect")}</p>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl font-semibold">{t("routes.auth.joinError")}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
              <Link
                to="/"
                className="mt-6 inline-flex rounded-full glass border border-border px-6 py-2.5 text-sm"
              >
                {t("routes.auth.joinBack")}
              </Link>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
